import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { selectSessionId } from '../store/chatSlice';
import { PHOTO_FILTER } from '../utils/colorInversion';
import {
  extensionLabel, fetchAttachmentLink, formatFileSize, isImageAttachment,
} from '../utils/attachments';

// Files sent in a live chat. A batch from one person sits in its own bubble, in that person's
// colours (client blue-grey on the right, agent green on the left), with a "4 files" header
// and arrows when the row of small square tiles is wider than the bubble. Images show a
// thumbnail; other files their type and name. A tile opens the file through a fresh link
// each time (none is stored), so files in older chats still open.
// Borders rather than shadows, so it still reads when the client's platform inverts colours.

const TILE = 88;

// Same shape and colours as the text bubbles (MessageBubble), so whose files they are is clear
const FileBubble = styled.div`
  max-width: 78%;
  width: fit-content;
  min-width: 0;
  box-sizing: border-box;
  padding: 6px 6px 8px;
  border-radius: ${props => (props.$isUser
    ? `16px ${props.$first ? '16px' : '4px'} 4px 16px`
    : `${props.$first ? '16px' : '4px'} 16px 16px 4px`)};
  background: ${props => (props.$isUser ? '#e7edf3' : props.$agent ? '#e8f5ee' : '#ffffff')};
  border: 1px solid ${props => (props.$isUser ? '#c3d3e6' : props.$agent ? '#bfe0cc' : '#d9e0e8')};

  @media (max-width: 480px) {
    max-width: 85%;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 22px;
  padding: 0 2px 5px 4px;
  font-size: 11.5px;
  font-weight: 600;
  color: ${props => (props.$isUser ? '#275386' : props.$agent ? '#166534' : '#475569')};

  svg.clip {
    width: 13px;
    height: 13px;
    flex-shrink: 0;
  }
`;

const Count = styled.span`
  flex: 1;
  white-space: nowrap;
`;

const Arrow = styled.button`
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid ${props => (props.$isUser ? '#c3d3e6' : '#bfe0cc')};
  border-radius: 50%;
  background: #ffffff;
  color: #275386;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  svg {
    width: 11px;
    height: 11px;
  }

  &:disabled {
    color: #cbd5e1;
    cursor: default;
  }
`;

// The tiles, scrolling sideways inside the bubble (never past it)
export const TileRow = styled.div`
  display: flex;
  gap: 6px;
  max-width: 100%;
  overflow-x: auto;
  scroll-behavior: smooth;
  scroll-snap-type: x proximity;
  padding-bottom: 4px;
  scrollbar-width: thin;
  scrollbar-color: #94a3b8 transparent;

  &::-webkit-scrollbar {
    height: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: #94a3b8;
    border-radius: 3px;
  }

  & > * {
    scroll-snap-align: start;
  }
`;

export const TileBox = styled.button`
  position: relative;
  flex: 0 0 auto;
  width: ${props => props.$size || TILE}px;
  height: ${props => props.$size || TILE}px;
  padding: 0;
  border: 1px solid ${props => (props.$error ? '#fca5a5' : '#d9e0e8')};
  border-radius: 10px;
  overflow: hidden;
  background: #ffffff;
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  font: inherit;
  color: inherit;

  &:hover:not(:disabled) {
    border-color: #275386;
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    /* Undoes the client platform's dark-mode inversion, when it tells us (utils/colorInversion) */
    filter: ${PHOTO_FILTER};
  }
`;

const DocFace = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  height: 100%;
  padding: 8px 6px;
  box-sizing: border-box;

  svg {
    width: ${props => (props.$small ? 18 : 22)}px;
    height: ${props => (props.$small ? 18 : 22)}px;
    flex-shrink: 0;
  }
`;

const Badge = styled.span`
  padding: 1px 6px;
  border-radius: 6px;
  background: #275386;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3px;
`;

const TileName = styled.span`
  max-width: 100%;
  font-size: 10.5px;
  line-height: 1.25;
  color: #1b3c66;
  text-align: center;
  word-break: break-all;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// Sending / not sent, over the tile
const Overlay = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px;
  box-sizing: border-box;
  background: ${props => (props.$error ? 'rgba(254, 242, 242, 0.94)' : 'rgba(255, 255, 255, 0.72)')};
  color: ${props => (props.$error ? '#b91c1c' : '#275386')};
  font-size: 10.5px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
`;

const Spinner = styled.span`
  width: 18px;
  height: 18px;
  border: 2px solid #c3d3e6;
  border-top-color: #275386;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const FailNote = styled.div`
  padding: 4px 4px 0;
  font-size: 11px;
  color: #b91c1c;
`;

export const DocumentIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" stroke="#275386" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M14 3v5h5M9 13h6M9 17h4" stroke="#275386" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ClipIcon = () => (
  <svg className="clip" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M21 11.5l-8.6 8.6a5.5 5.5 0 0 1-7.8-7.8l8.6-8.6a3.7 3.7 0 0 1 5.2 5.2l-8.6 8.6a1.8 1.8 0 0 1-2.6-2.6l8-8"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Chevron = ({ left }) => (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d={left ? 'M7.5 2.5L4 6l3.5 3.5' : 'M4.5 2.5L8 6 4.5 9.5'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// The type badge and name, for a file without a thumbnail
export const DocumentFace = ({ name, small = false }) => (
  <DocFace $small={small}>
    <DocumentIcon />
    <Badge>{extensionLabel(name)}</Badge>
    {!small && <TileName>{name}</TileName>}
  </DocFace>
);

// One sent file. item = {attachment, uploading, uploadError}
const AttachmentTile = ({ item }) => {
  const sessionId = useSelector(selectSessionId);
  const { attachment, uploading, uploadError } = item;
  const [thumbUrl, setThumbUrl] = useState(null);
  const [thumbFailed, setThumbFailed] = useState(false);
  const [openFailed, setOpenFailed] = useState(false);
  const ready = Boolean(attachment?.id) && !uploading && !uploadError;
  const wantsThumb = ready && isImageAttachment(attachment) && !thumbFailed;

  useEffect(() => {
    if (!wantsThumb || thumbUrl || !sessionId) return undefined;
    let cancelled = false;
    fetchAttachmentLink(sessionId, attachment.id)
      .then(url => { if (!cancelled) setThumbUrl(url); })
      .catch(() => { if (!cancelled) setThumbFailed(true); });
    return () => { cancelled = true; };
  }, [wantsThumb, thumbUrl, sessionId, attachment?.id]);

  const name = attachment?.file_name || 'File';

  const openFile = async () => {
    if (!ready) return;
    setOpenFailed(false);
    // Opened before the link arrives, so the browser doesn't block it as a pop-up
    const tab = window.open('', '_blank');
    try {
      const url = await fetchAttachmentLink(sessionId, attachment.id);
      if (tab) {
        tab.opener = null;
        tab.location.href = url;
      } else {
        window.location.assign(url);
      }
    } catch (e) {
      if (tab) tab.close();
      setOpenFailed(true);
    }
  };

  const size = formatFileSize(attachment?.file_size);
  const title = uploadError || (openFailed ? "Couldn't open the file. Tap to try again." : `${name}${size ? ` · ${size}` : ''}`);

  return (
    <TileBox
      type="button"
      onClick={openFile}
      disabled={!ready}
      $error={Boolean(uploadError || openFailed)}
      title={title}
      aria-label={ready ? `Open ${name}` : name}
    >
      {wantsThumb && thumbUrl
        ? <img src={thumbUrl} alt={name} onError={() => setThumbFailed(true)} />
        : <DocumentFace name={name} />}
      {uploading && <Overlay><Spinner />Sending</Overlay>}
      {uploadError && <Overlay $error>Not sent</Overlay>}
      {openFailed && !uploadError && <Overlay $error>Tap to retry</Overlay>}
    </TileBox>
  );
};

// A message's files (one, or a run sent together) in one bubble
const AttachmentTiles = ({ items, isUser, isAgent, isFirstInGroup = true }) => {
  const rowRef = useRef(null);
  const [scroll, setScroll] = useState({ left: false, right: false });

  // Whether there is more to either side, for the arrows
  const measure = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;
    const left = row.scrollLeft > 2;
    const right = row.scrollLeft + row.clientWidth < row.scrollWidth - 2;
    setScroll(current => (current.left === left && current.right === right ? current : { left, right }));
  }, []);

  useEffect(() => {
    measure();
    const row = rowRef.current;
    if (!row || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [measure, items.length]);

  const scrollBy = (direction) => {
    rowRef.current?.scrollBy({ left: direction * (TILE + 6) * 2, behavior: 'smooth' });
  };

  const failed = items.find(item => item.uploadError);
  const sending = items.filter(item => item.uploading).length;
  const count = items.length === 1 ? '1 file' : `${items.length} files`;
  const overflowing = scroll.left || scroll.right;

  return (
    <FileBubble $isUser={isUser} $agent={isAgent} $first={isFirstInGroup}>
      <Header $isUser={isUser} $agent={isAgent}>
        <ClipIcon />
        <Count>{sending ? `${count} · sending ${sending}` : count}</Count>
        {overflowing && (
          <>
            <Arrow type="button" $isUser={isUser} onClick={() => scrollBy(-1)} disabled={!scroll.left} aria-label="Scroll left">
              <Chevron left />
            </Arrow>
            <Arrow type="button" $isUser={isUser} onClick={() => scrollBy(1)} disabled={!scroll.right} aria-label="Scroll right">
              <Chevron />
            </Arrow>
          </>
        )}
      </Header>
      <TileRow ref={rowRef} onScroll={measure}>
        {items.map(item => <AttachmentTile key={item.key} item={item} />)}
      </TileRow>
      {failed && <FailNote>{failed.uploadError}</FailNote>}
    </FileBubble>
  );
};

export default AttachmentTiles;
